import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from './api/client.js';

/**
 * Agent-facing scripting/guidance TEXT catalogue (#637, CCF-113, SCRN-56).
 *
 * PTSB can reword this copy from the Admin Console without a code release. The
 * BFF resolves each editable key to a plain string (baked-in default + any
 * admin override); this provider fetches the resolved map once at app boot and
 * `useContent(key)` reads it.
 *
 * **First paint is never empty.** The catalogue defaults are compiled in here
 * (`CONTENT_DEFAULTS`), so a screen renders its default text immediately and
 * before the `/content` fetch resolves, on a cold start, or if the fetch fails.
 * The fetched map only ever *replaces* a default with the (possibly reworded)
 * resolved value. An unknown key falls back to the compiled default, then to
 * the empty string — a missing key can never crash a screen.
 *
 * Render values as PLAIN TEXT only (no `dangerouslySetInnerHTML`) — admin-
 * supplied copy must never go through an HTML renderer (stored-XSS). Line
 * breaks are preserved via `whitespace-pre-line` at the call site.
 */

/**
 * The baked-in defaults, compiled into the SPA so first paint is never empty.
 * These MUST match the server catalogue (`app/server/src/content/catalogue.ts`)
 * defaults for the keys the SPA consumes; the server remains the source of
 * truth and its resolved value wins once fetched.
 */
export const CONTENT_DEFAULTS: Readonly<Record<string, string>> = {
  // Increment 29 (#637, SCRN-01) — the ID&V "Need help?" box heading. Ships
  // compiled-in so it renders before the override fetch resolves (never blank).
  'idv.help.title': 'Need help?',
  'idv.help.body':
    'If you’re experiencing difficulties with customer verification contact your supervisor for assistance.',
  // Increment 18 (#637, SCRN-01 / CCF-11) — the ID&V "Stop and Think" fraud-alert
  // modal guidance copy. Ships compiled-in so the modal renders its default
  // wording before the override fetch resolves (fail-safe — never blank). Values
  // must match the server catalogue defaults exactly.
  'idv.stop_and_think.title': 'Attention Required: Stop and Think',
  'idv.stop_and_think.unavailable':
    'The fraud advisory checks are temporarily unavailable. Acknowledge to proceed.',
  'idv.stop_and_think.acknowledge_label':
    'I have read and understood this message',
  'security.medium.fail.heading':
    'Sorry, the information you gave us does not match our records. You will need to visit a branch with a photo ID and your account details.',
  'security.medium.fail.note':
    'Please leave a note that customer has been referred to branch.',
  'broker.medium.security.fail.heading':
    'Sorry, the information you gave us does not match our records, so security has failed.',
  'broker.medium.security.fail.advisory':
    'Advise the broker to contact their Broker Representative / IMC.',
  // #754: align ECL Medium Security failure scripting to the standard
  // Medium/High wording (#544) already adopted by ECL High (#674).
  'ecl.medium.security.fail.advisory':
    'Sorry, the information you gave us does not match our records. You will need to visit a branch with a photo ID and your account details.',
  'ecl.medium.security.fail.remark':
    'Please leave a note that customer has been referred to branch.',
  // #674 (CCRS-3491): ECL High Security failure scripting aligned to the
  // (already-correct) EDS High Security wording (`security.high.fail.*`).
  'ecl.high.security.fail.advisory':
    'Sorry, the information you gave us does not match our records. You will need to visit a branch with a photo ID and your account details.',
  'ecl.high.security.fail.remark':
    'Please leave a note that customer has been referred to branch.',
  'chequebook.order.returns_block':
    'The chequebook cannot be ordered at this time due to account status, please check account holds.',
  'chequebook.order.already_ordered':
    'Chequebook already ordered today. Please try again tomorrow.',
  'security.high.fail.heading':
    'Sorry, the information you gave us does not match our records. You will need to visit a branch with a photo ID and your account details.',
  'security.high.fail.note':
    'Please leave a note that customer has been referred to branch.',
  'outbound.security.dob_fail':
    'The date of birth given does not match our records. Advise the customer you are unable to proceed.',
  'outbound.security.recording_declined':
    'The customer has not agreed to the call being recorded. Advise the customer you are unable to continue and politely end the call.',
  'outbound.security.not_suitable':
    'Now is not a suitable time. Ask the customer for a more suitable time and schedule a call back.',
  'card.activation.blocked':
    'The bank could not activate this card — its status at the bank prevents activation (gateway technical condition). This cannot be corrected from this app; raise it with the card operations team to fix the card record.',
  'card.activation.not_activatable':
    'This card is not new or replaced, so the bank will not activate it — it may already be active. No activation is needed.',
  'card.activation.too_early':
    'The card cannot be activated yet — its activation date at the bank has not been reached. Ask the customer to try again on or after that date.',
  'card.pin.already_updated_today':
    'This card was already updated at the bank today — a further update cannot be made until tomorrow. No action is needed; ask the customer to retry after midnight.',
  'registration.mandatory_script':
    'I’ll transfer you now to our automated Self Service to choose your ' +
    'personal access number and get your temporary internet password. PTSB ' +
    'will never ask you to confirm the full six digits of your Personal Access ' +
    'Number; we will only ever ask you for three random digits from this ' +
    'number. PTSB will also never contact you by email or text message asking ' +
    'you to confirm or update any personal details.',
  // Increment 11 (#637, SCRN-03) — the shared Security screen knowledge-question
  // LABELS. Each mirrors its server-catalogue default verbatim (text only, never
  // logic); a screen renders the default until an admin override is fetched.
  'security.question.full_name': 'Full name:',
  'security.question.dob': 'Date of birth:',
  'security.question.mothers_maiden_name': 'Mother’s maiden name:',
  'security.question.mobile_number': 'Mobile number:',
  'security.question.recurring_payment':
    'Do you have any recurring payments that go out of your account each month?',
  'security.question.recent_transaction':
    'Can you confirm a recent transaction on the account?',
  'security.question.joint_accounts':
    'Have you any joint accounts held in PTSB?',
  'security.question.visa_credit_limit': 'Credit limit on Visa credit card',
  'security.question.visa_cardholders':
    'Any other card holders on the account?',
  'security.question.visa_card_expiry':
    'Confirm the expiry date on the Visa credit card (check TSYS):',
  'security.question.property_address':
    'Full property address or correspondence address',
  'security.question.mortgage_year':
    'Can you confirm the year your mortgage was drawn down?',
  'security.question.mortgage_term':
    'Can you confirm the original length of your mortgage term?',
  'security.question.mortgage_broker_branch':
    'Can you confirm if you took your mortgage out with a branch or a broker?',
  'security.question.home_insurance':
    'Can you confirm the company of your home insurance provider?',
  'security.question.life_assurance':
    'Can you confirm your life assurance provider?',
  'security.question.direct_debit_bank':
    'Can you confirm the bank that your direct debit is deducted from? (Only valid question if customer has direct debit)',
  'security.question.mortgage_repayment_protection':
    'Do you pay a mortgage repayment protection policy with your mortgage?',
  'security.question.first_school':
    'Can you confirm the first school you attended?',
  'security.question.favourite_music':
    'Can you confirm your favourite type of music?',
  'security.question.eye_colour': 'Can you confirm your eye colour?',
  // Increment 12 (#637, SCRN-11 / CCF-113) — the regulatory OUTBOUND opening
  // call script (OutboundSecurityScreen). Each mirrors its server-catalogue
  // default verbatim (text only, never logic). The greeting/intro carry literal
  // placeholder tokens substituted with the live values at render.
  'outbound.script.regulatory_heading':
    'Please ensure the outbound scripting below is followed in the exact order. This is a regulatory requirement.',
  'outbound.script.greeting': 'Hi can I speak to {scriptFullName} please?',
  'outbound.script.intro':
    'Hi {scriptFirstName}, my name is {scriptAgentName}, I’m calling from PTSB in relation to…',
  'outbound.script.suitable_time':
    'Is now a suitable time to speak for a moment?',
  'outbound.script.confirm_dob':
    'For security purposes, can I ask you to confirm your Date of Birth please?',
  // Increment 13 (#637, SCRN-26) — the "New Mobile update change template"
  // email wording (NEW_MOBILE_NUMBER_UPDATE). Each mirrors its server-catalogue
  // default verbatim (text only, never logic): only the fixed sentences,
  // subject and field labels are editable; the layout + auto-filled values stay
  // in code. Threaded into the template via `useContentMap()` at the Mobile
  // Update screen's render site.
  'email.mobile_update.subject': 'New Mobile update change template',
  'email.mobile_update.instruction_do_not_process':
    'Do not process any mobile number update on any system.',
  'email.mobile_update.instruction_send_team_lead':
    'Complete the below request and send to your Team Lead and CC Aaron Kearney',
  'email.mobile_update.instruction_weekend':
    'Weekend updates to be sent to Niamh Vaughan with Team Lead CC’d.',
  'email.mobile_update.customer_advisory':
    'Customer should be informed that we do not change mobile numbers on the call, and that the request will be sent to the relevant Department for processing and this could take 24 hours.',
  'email.mobile_update.turnaround_note':
    'Mobile numbers will be updated within 24 hours for the customer.',
  'email.mobile_update.update_instruction':
    'Please update the mobile number on Unibanks and TSYS for the following Customer(s)',
  'email.mobile_update.label_customer_name': 'Customer Name:',
  'email.mobile_update.label_customer_cif': 'Customer CIF:',
  'email.mobile_update.label_card_number': 'Card Number(s):',
  'email.mobile_update.label_new_mobile_number': 'New Mobile Number:',
  'email.mobile_update.label_listened': 'Name of person who listened to call:',
  'email.mobile_update.label_reason': 'Reason for the change of mobile number:',
  // Increment 14 (#637, SCRN-42) — the two hold-removal email templates'
  // wording (R_HOLD_REMOVAL / N_HOLD_REMOVAL). Each mirrors its server-catalogue
  // default verbatim (text only, never logic): only the subject/title stems, the
  // fixed sentences and field labels are editable; the layout + auto-filled
  // values stay in code. Threaded into the templates via `useContentMap()` at
  // the Account Holds screen's render site.
  'email.hold_removal_r.subject': 'R hold removal draft',
  'email.hold_removal_r.title': 'R hold Removal',
  'email.hold_removal_r.instruction_reviewed':
    'Please can you remove the R hold on the following account, I have fully reviewed the account for the last 3 months',
  'email.hold_removal_r.instruction_no_returns':
    'I can confirm the customer has no returned Direct Debits or cheques in the last 3 months',
  'email.hold_removal_r.label_customer_name': 'Customer Name:',
  'email.hold_removal_r.label_account_number': 'Account Number:',
  'email.hold_removal_n.subject': 'Request for N Hold Removal - Template',
  'email.hold_removal_n.title': 'Request for N Hold removal:',
  'email.hold_removal_n.label_account_number': 'Account Number:',
  'email.hold_removal_n.paid_in_full':
    'There is an N Hold on the above account and customer has paid the outstanding balance in full.',
  'email.hold_removal_n.closing': 'Can this N Hold please be removed?',
  // Increment 19 (#637, SCRN-26) — the SA Accounts Callback email template's
  // wording (SA_ACCOUNTS_CALLBACK). Mirrors its server-catalogue default
  // verbatim (text only, never logic): only the subject, title, the two Y/N
  // update lines, the field labels and the closing advisory are editable; the
  // layout + auto-filled values stay in code. Threaded into the template via
  // `useContentMap()` at the Change Mobile Number screen's render site.
  'email.sa_accounts_callback.subject':
    'SA Accounts - Change Personal Details Request',
  'email.sa_accounts_callback.title':
    'Enhanced Customer Support Callback – South African Accounts',
  'email.sa_accounts_callback.label_mobile_update': 'Mobile Number Update:',
  'email.sa_accounts_callback.label_address_update': 'Address Update:',
  'email.sa_accounts_callback.yn': 'Y/N',
  'email.sa_accounts_callback.label_customer_name': 'Customer Name:',
  'email.sa_accounts_callback.label_open24_number': 'Open24 Number:',
  'email.sa_accounts_callback.label_new_mobile_number':
    'New Mobile Number (if applicable):',
  'email.sa_accounts_callback.label_new_address':
    'New Address (if applicable):',
  'email.sa_accounts_callback.callback_advisory':
    'Please advise the customer they will receive a call back before 17:00 the following working day',
};

type ContentMap = Readonly<Record<string, string>>;

const ContentContext = createContext<ContentMap>(CONTENT_DEFAULTS);

export function ContentProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [content, setContent] = useState<ContentMap>(CONTENT_DEFAULTS);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.GET('/content');
        if (!cancelled && data?.content) {
          // Merge over the compiled defaults so any key the server omits still
          // resolves; the server value wins for keys it returns.
          setContent({ ...CONTENT_DEFAULTS, ...data.content });
        }
      } catch {
        // Fail-safe: keep the compiled defaults. An agent never sees blank copy.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ContentContext.Provider value={content}>
      {children}
    </ContentContext.Provider>
  );
}

/**
 * The resolved text for a catalogue key. Falls back to the compiled default,
 * then to the empty string — never throws for an unknown key.
 */
export function useContent(key: string): string {
  const content = useContext(ContentContext);
  return content[key] ?? CONTENT_DEFAULTS[key] ?? '';
}

/**
 * The whole resolved content map (compiled defaults merged with any fetched
 * admin overrides). For a screen that resolves MANY keys at once — e.g. the
 * shared Security screen's per-question labels (#637 inc 11) — reading the map
 * once and looking keys up (falling back to a baked-in default) is simpler than
 * calling {@link useContent} per key. Prefer `useContent` for one-off reads.
 */
export function useContentMap(): ContentMap {
  return useContext(ContentContext);
}
